import { TableOutlined } from '@ant-design/icons'
import {
  buildUuid,
  CreatePanelRequest,
  DRILLDOWN_PANEL_INITIAL_DATA,
  DrilldownPanelInitialDataMap,
} from 'features/drilldown'
import {
  AbstractDrilldownPanel,
  DrilldownPanelProps,
} from 'features/drilldown/components/DrilldownPanel'
import { HistogramConfigurationPanel } from 'features/drilldown/components/histogram/configPanel/HistogramConfigurationPanel'
import {
  HistogramConfigurationContextProvider,
  HistogramConfigurationProviderProps,
} from 'features/drilldown/components/histogram/contexts/HistogramConfigurationContext'
import { DrilldownHistogram } from 'features/drilldown/components/histogram/DrilldownHistogram'
import { PanelContextProvider } from 'features/drilldown/components/PanelContext'
import {
  DrilldownTableConfigContextProvider,
  DrilldownTableConfigContextProviderProps,
} from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableConfigPanel } from 'features/drilldown/components/table/config/DrilldownTableConfigPanel'
import { DrilldownTable } from 'features/drilldown/components/table/DrilldownTable'
import { DrilldownPanelType } from 'features/drilldown/types'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'
import React, { FC, ReactNode } from 'react'

import { DrilldownPanelConfig } from './PanelConfigs'

export const buildPanel = ({
  panelType,
  initialDataOverride,
}: CreatePanelRequest) => {
  const initialData = merge(
    cloneDeep(DRILLDOWN_PANEL_INITIAL_DATA[panelType]),
    initialDataOverride
  )
  return {
    id: buildUuid(),
    panelType,
    panelData: initialData,
    filters: new Map(),
    focusItems: undefined,
  }
}

export const PanelRender = ({
  children,
  panelType,
  ...rest
}: DrilldownPanelProps & {
  panelType: DrilldownPanelType
  children: ReactNode
}) => (
  <PanelContextProvider>
    <AbstractDrilldownPanel {...rest} panelType={panelType}>
      {children}
    </AbstractDrilldownPanel>
  </PanelContextProvider>
)

export const createPanelConfig = <T extends keyof DrilldownPanelInitialDataMap>(
  panelType: T,
  initialData: DrilldownPanelInitialDataMap[T],
  Component: React.FC,
  options: {
    isGloballyAvailable: boolean
    icon?: ReactNode
    titleTooltip?: ReactNode
    configPanel?: ReactNode
    panelPadding?: number
    componentProps?: Record<string, unknown>
  }
): DrilldownPanelConfig => ({
  name: panelType,
  icon: options?.icon ?? <TableOutlined />,
  tooltip: initialData.title,
  isGloballyAvailable: options.isGloballyAvailable,
  render: (props) => (
    <PanelRender
      {...props}
      panelType={panelType}
      titleTooltip={options.titleTooltip}
      configPanel={options.configPanel}
      panelPadding={options.panelPadding}>
      <Component {...options.componentProps} />
    </PanelRender>
  ),
})

export const HistogramPanelRender: FC<{
  panelProps: DrilldownPanelProps
  histoConfigProps: HistogramConfigurationProviderProps
}> = ({ panelProps, histoConfigProps }) => {
  return (
    <HistogramConfigurationContextProvider {...histoConfigProps}>
      <PanelRender
        {...panelProps}
        panelType={panelProps.panelType}
        configPanel={<HistogramConfigurationPanel />}>
        <DrilldownHistogram />
      </PanelRender>
    </HistogramConfigurationContextProvider>
  )
}

export const TablePanelRender: FC<{
  panelProps: DrilldownPanelProps
  configProps: Omit<DrilldownTableConfigContextProviderProps, 'children'>
}> = ({ panelProps, configProps }) => {
  return (
    <DrilldownTableConfigContextProvider {...configProps}>
      <PanelRender
        {...panelProps}
        panelType={panelProps.panelType}
        configPanel={<DrilldownTableConfigPanel />}>
        <DrilldownTable />
      </PanelRender>
    </DrilldownTableConfigContextProvider>
  )
}
